import {useRef, useState, useCallback} from 'react';
import {
    View,
    Text,
    StyleSheet,
    Animated,
    RefreshControl,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {BookOpen, Award, User, Crown, ShoppingBag, QrCode, MessageSquare} from 'lucide-react-native';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useOrganizationData} from "@/data/app-data-hooks";
import {ThemeType} from "@/theming/baseTheme";
import {DebugDataButton} from '@/components/common/DebugDataButton';
import {QuickActionsGrid, QuickAction} from "@/components/common/QuickActionsGrid";
import {AnimatedHeader, HEADER_CONSTANTS} from "@/components/organization/AnimatedHeader";
import {HorizontalVideoListComponent} from "@/components/media/HorizontalVideoListComponent";
import {AchievementVerticalList} from "@/components/achievement/AchievementVerticalList";
import {appDataKeys} from '@/data/app-data-keys';
import {useBookmarkedVideos} from "@/hooks/useBookmarkedVideos";
import {VideoSource} from "@/components/media/VideoSource";
import {useLastViewedVideos} from "@/hooks/useLastViewedVideos";
import {OpenWebAppButton} from "@/components/common/OpenWebAppButton";

interface OrganizationDashboardProps {
    orgId: string;
    onNavigateToMedia: () => void;
    onNavigateToAchievements: () => void;
    onNavigateToProfile: () => void;
    onNavigateToMembership: () => void;
    onNavigateToOffers?: () => void;
    onNavigateToChat?: () => void;
    onNavigateToInvite?: () => void;
    onNavigateToAllVideos: () => void;
    onPlayVideo: (videoId: string, source: VideoSource) => void;
    onNavigateToBookmarkedVideos: () => void;
    onNavigateToLastViewedVideos: () => void;
    onSwitchOrg?: () => void;
    orgCount?: number;
}

export const OrganizationDashboard = ({
                                          orgId,
                                          onNavigateToMedia,
                                          onNavigateToAchievements,
                                          onNavigateToProfile,
                                          onNavigateToMembership,
                                          onNavigateToOffers,
                                          onNavigateToChat,
                                          onNavigateToInvite,
                                          onNavigateToAllVideos,
                                          onPlayVideo,
                                          onNavigateToBookmarkedVideos,
                                          onNavigateToLastViewedVideos,
                                          onSwitchOrg,
                                          orgCount,
                                      }: OrganizationDashboardProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const queryClient = useQueryClient();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme);
    const {data: orgData} = useOrganizationData(orgId);

    // Animated values for collapsing header
    const scrollY = useRef(new Animated.Value(0)).current;

    // Refresh state
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Header height calculations
    const HEADER_MAX_HEIGHT = HEADER_CONSTANTS.MAX_HEIGHT_OFFSET + insets.top;

    // Handle pull-to-refresh
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await queryClient.invalidateQueries({queryKey: appDataKeys.all});
        } finally {
            setIsRefreshing(false);
        }
    }, [queryClient]);

    const bookmarkedVideos = useBookmarkedVideos(orgId);
    const lastViewedVideos = useLastViewedVideos(orgId);

    if (!orgData) {
        return (
            <View style={styles.container}>
                <Text style={styles.errorText}>{t('dashboard.error.orgNotFound')}</Text>
            </View>
        );
    }

    const {organization, achievements, media} = orgData;

    // Filter featured videos (first 5 video items)
    const featuredVideos = media.featured.map(id => media.items[id] ?? null).filter(Boolean).slice(0, 5);
    const featuredVideosCount = media.featured.length;

    const selectedBookmarkedVideos = bookmarkedVideos.videos.slice(0, 5);
    const bookmarkedVideosCount = bookmarkedVideos.count;

    // Get recent achievements (last 3)
    const recentAchievements = achievements
        .sort((a, b) => new Date(b.assigned || 0).getTime() - new Date(a.assigned || 0).getTime())
        .slice(0, 3);

    // Calculate stats for content sections
    const totalVideos = Object.values(media.items).filter(item => item.type === 'video').length;

    // Quick actions configuration - build dynamically to include offers if handler provided
    const quickActions: QuickAction[] = [
        {
            id: 'media',
            icon: <BookOpen size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.media.title'),
            subtitle: t('dashboard.quickActions.media.subtitle'),
            onPress: onNavigateToMedia,
        },
        {
            id: 'achievements',
            icon: <Award size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.achievements.title'),
            subtitle: t('dashboard.quickActions.achievements.subtitle'),
            onPress: onNavigateToAchievements,
        },
        {
            id: 'profile',
            icon: <User size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.profile.title'),
            subtitle: t('dashboard.quickActions.profile.subtitle'),
            onPress: onNavigateToProfile,
        },
        {
            id: 'membership',
            icon: <Crown size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.membership.title'),
            subtitle: t('dashboard.quickActions.membership.subtitle'),
            onPress: onNavigateToMembership,
        },
    ];

    // Add offers action if handler is provided
    if (onNavigateToOffers) {
        quickActions.push({
            id: 'offers',
            icon: <ShoppingBag size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.offers.title'),
            subtitle: t('dashboard.quickActions.offers.subtitle'),
            onPress: onNavigateToOffers,
        });
    }

    // Add chat action if handler is provided
    if (onNavigateToChat) {
        quickActions.push({
            id: 'chat',
            icon: <MessageSquare size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.chat.title'),
            subtitle: t('dashboard.quickActions.chat.subtitle'),
            onPress: onNavigateToChat,
        });
    }

    // Add invite action if handler is provided (owners/leaders only)
    if (onNavigateToInvite) {
        quickActions.push({
            id: 'invite',
            icon: <QrCode size={24} color={theme.colors.primary}/>,
            title: t('dashboard.quickActions.invite.title'),
            subtitle: t('dashboard.quickActions.invite.subtitle'),
            onPress: onNavigateToInvite,
        });
    }

    return (
        <View style={styles.container}>
            {/* Animated Header */}
            <AnimatedHeader
                organization={organization}
                scrollY={scrollY}
                onSwitchOrg={onSwitchOrg}
                orgCount={orgCount}
            />

            {/* Scrollable Content */}
            <Animated.ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{nativeEvent: {contentOffset: {y: scrollY}}}],
                    {useNativeDriver: false}
                )}
                scrollEventThrottle={16}
                contentContainerStyle={{paddingTop: HEADER_MAX_HEIGHT}}
                refreshControl={
                    <RefreshControl
                        refreshing={isRefreshing}
                        onRefresh={handleRefresh}
                        tintColor={theme.colors.primary}
                        colors={[theme.colors.primary]}
                        progressViewOffset={HEADER_MAX_HEIGHT}
                    />
                }
            >
                <HorizontalVideoListComponent
                    videos={featuredVideos}
                    title={t('dashboard.sections.featuredVideos')}
                    actionText={`${t('dashboard.sections.viewAllAction')} (${featuredVideosCount})`}
                    onActionPress={onNavigateToAllVideos}
                    onPlayVideo={(id) => onPlayVideo(id, 'featured')}
                />

                {lastViewedVideos.count > 0 &&
                    <HorizontalVideoListComponent
                        videos={lastViewedVideos.videos}
                        title={t('dashboard.sections.lastViewed')}
                        onPlayVideo={(id) => onPlayVideo(id, 'last-viewed')}
                        actionText={`${t('dashboard.sections.viewAllAction')} (${lastViewedVideos.count})`}
                        onActionPress={onNavigateToLastViewedVideos}
                    />
                }

                {bookmarkedVideos.count > 0 &&
                    <HorizontalVideoListComponent
                        videos={selectedBookmarkedVideos}
                        title={t('dashboard.sections.bookmarkedVideos')}
                        actionText={`${t('dashboard.sections.viewAllAction')} (${bookmarkedVideosCount})`}
                        onActionPress={onNavigateToBookmarkedVideos}
                        onPlayVideo={(id) => onPlayVideo(id, 'favorites')}
                    />
                }

                <AchievementVerticalList
                    achievements={recentAchievements}
                    title={t('dashboard.sections.recentAchievements')}
                    actionText={t('dashboard.sections.viewAllAction')}
                    onActionPress={onNavigateToAchievements}
                    backgroundColor={`${theme.colors.background}CC`}
                />

                <QuickActionsGrid actions={quickActions}>
                </QuickActionsGrid>

                <OpenWebAppButton orgId={orgId} styleOverride={styles.openWebButton}/>
            </Animated.ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    openWebButton: {
        paddingHorizontal: theme.spacing.md,
        marginBottom: theme.spacing.md,
    },
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    errorText: {
        color: theme.colors.error,
        fontSize: theme.fontSize.md,
        textAlign: 'center',
        marginTop: theme.spacing.xl,
    },
    scrollView: {
        flex: 1,
    },
});
